import { useEffect, useLayoutEffect, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import ArticlePage from './components/ArticlePage'
import ContentArchive from './components/ContentArchive'
import ContentImage from './components/ContentImage'
import Layout from './components/Layout'
import MediaStrip from './components/MediaStrip'
import PageHero from './components/PageHero'
import QuoteForm from './components/QuoteForm'
import ResponsiveImage from './components/ResponsiveImage'
import contentIndex from './data/content-index.generated.json'
import { contact, projects, services } from './data/site'
import { openWhatsAppWidget, WHATSAPP_WIDGET_PANEL_ID } from './lib/whatsapp'

gsap.registerPlugin(ScrollTrigger)

const contentBySlug = new Map(contentIndex.entries.map((entry) => [entry.slug, entry]))
const allPosts = contentIndex.entries.filter(({ kind }) => kind === 'post')
const projectPosts = allPosts.filter(({ category }) => category === 'Proyectos y noticias')
const technicalPosts = allPosts.filter(({ category }) => category === 'Info técnica')

const openContactWhatsApp = (event) => {
  event.preventDefault()
  openWhatsAppWidget({ clickLocation: 'contact_options', source: 'contact_page' })
}

function ArrowLink({ href, children, light = false, external = false, analyticsLocation = 'inline_cta' }) {
  return (
    <a className={`arrow-link ${light ? 'light' : ''}`} href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} data-analytics-event="cta_click" data-analytics-location={analyticsLocation}>
      <span>{children}</span>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19 19 5M8 5h11v11" /></svg>
    </a>
  )
}

function SectionHeading({ eyebrow, title, body }) {
  return (
    <div className="section-heading" data-reveal>
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {body && <p className="section-description">{body}</p>}
    </div>
  )
}

function HomePage() {
  return (
    <>
      <section className="home-hero">
        <ResponsiveImage name="equipo-en-obra" alt="Equipo CRS ejecutando una cimentación profunda en una zona montañosa" className="hero-image" eager />
        <div className="hero-shade" />
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-copy">
          <p className="eyebrow light" data-hero>Ingeniería especializada / México</p>
          <h1 data-hero>servicio y renta de equipo para <strong>cimentaciones profundas</strong></h1>
          <div className="hero-bottom" data-hero>
            <p>Seguridad, calidad y servicio en cimentaciones profundas para proyectos que no admiten improvisaciones.</p>
            <ArrowLink href="/servicios/" light>Explorar servicios</ArrowLink>
          </div>
        </div>
        <div className="hero-side-label" aria-hidden="true">CRS · CIMENTACIONES PROFUNDAS · 2026</div>
        <div className="scroll-cue" aria-hidden="true"><span>Desliza</span><i /></div>
      </section>

      <section className="intro-section texture-grid">
        <div className="intro-number" data-reveal>01</div>
        <div className="intro-title" data-reveal>
          <p className="eyebrow">El valor bajo la superficie</p>
          <h2>Nueva generación<br />de <em>ingenieros.</em></h2>
        </div>
        <div className="intro-copy" data-reveal>
          <p>Combinamos experiencia comprobada, tecnología y ejecución en campo para resolver cimentaciones profundas con visión integral.</p>
          <p>Entendemos lo que hoy exigen los constructores: eficiencia, asesoría, seguridad y un equipo que responda.</p>
          <ArrowLink href="/nosotros/">Conocer a CRS</ArrowLink>
        </div>
      </section>

      <MediaStrip eyebrow="01.5 / Escala operativa" items={[
        { name: 'piloteadora-cielo', alt: 'Piloteadora CRS trabajando junto a una estructura industrial al atardecer', label: 'Capacidad de movilización' },
        { name: 'maquinaria-panoramica', alt: 'Perforadora CRS y cuadrilla en un frente de trabajo señalizado', label: 'Control de operación' },
        { name: 'obra-tijuana', alt: 'Plataforma de cimentación profunda con pilotes y equipo de concreto', label: 'Ejecución especializada' },
      ]} />

      <section className="services-section">
        <SectionHeading eyebrow="02 / Capacidades" title="Soluciones que llegan al fondo." body="Una operación especializada, desde el análisis hasta la ejecución." />
        <div className="service-list">
          {services.map((service) => (
            <article className="service-row" key={service.number} data-reveal>
              <span>{service.number}</span>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <i aria-hidden="true">↗</i>
            </article>
          ))}
        </div>
      </section>

      <section className="manifesto-section">
        <ResponsiveImage name="perforadora" alt="Especialistas CRS frente a perforadora hidráulica" className="manifesto-image" sizes="(min-width: 900px) 50vw, 100vw" />
        <div className="manifesto-copy texture-dots">
          <p className="eyebrow light" data-reveal>Rendimiento garantizado</p>
          <blockquote data-reveal>“La seguridad no es un requisito adicional. Es la forma correcta de construir.”</blockquote>
          <div className="manifesto-facts" data-reveal>
            <span>Maquinaria certificada</span>
            <span>Personal especializado</span>
            <span>Control en cada etapa</span>
          </div>
        </div>
      </section>

      <section className="projects-section texture-grid">
        <SectionHeading eyebrow="03 / Trabajo en campo" title="Proyectos que sostienen progreso." body="Experiencia en infraestructura, industria, edificación y obra pública a nivel nacional." />
        <div className="project-grid">
          {projects.map((project, index) => (
            <a className={`project-card project-card-${index + 1}`} href={project.href} key={project.title} data-reveal data-analytics-event="select_content" data-analytics-location="home_projects" data-analytics-content-type="project" data-analytics-content-id={project.href.replaceAll('/', '')} data-analytics-label={project.title}>
              <ResponsiveImage name={project.image} alt={`${project.title}, ${project.location}`} sizes="(min-width: 900px) 50vw, 100vw" />
              <div className="project-overlay">
                <span>{project.discipline}</span>
                <h3>{project.title}</h3>
                <p>{project.location}</p>
              </div>
            </a>
          ))}
        </div>
        <div className="section-action"><ArrowLink href="/proyectos/">Ver currículo de proyectos</ArrowLink></div>
      </section>

      <section className="process-section">
        <SectionHeading eyebrow="04 / Método" title="Precisión de principio a fin." />
        <ol className="process-grid">
          {[
            ['01', 'Analizar', 'Entender suelo, estructura, restricciones y programa.'],
            ['02', 'Diseñar', 'Traducir el reto en una estrategia ejecutable y segura.'],
            ['03', 'Movilizar', 'Asignar maquinaria, herramienta y personal especializado.'],
            ['04', 'Controlar', 'Medir avance, calidad y seguridad hasta la entrega.'],
          ].map(([number, title, copy]) => (
            <li key={number} data-reveal><span>{number}</span><h3>{title}</h3><p>{copy}</p></li>
          ))}
        </ol>
      </section>
    </>
  )
}

function AboutPage() {
  const values = [
    ['Integridad', 'Decidir y ejecutar con transparencia, incluso cuando nadie está mirando.'],
    ['Creatividad', 'Resolver las condiciones únicas de cada obra con experiencia e innovación.'],
    ['Trabajo en equipo', 'Integrarnos con clientes, proyectistas, supervisión y operación.'],
    ['Compromiso', 'Cumplir con seguridad, calidad, servicio y rendimiento en campo.'],
  ]
  return (
    <>
      <PageHero index="01" eyebrow="Quiénes somos" title="Ingeniería con" italic="criterio de campo." description="Somos una nueva generación de ingenieros en cimentación profunda: experiencia comprobada, pensamiento actual y ejecución responsable." image="equipo-en-obra" tags={['Experiencia', 'Criterio', 'Compromiso']} />
      <section className="split-story texture-grid">
        <SectionHeading eyebrow="Misión" title="Convertir complejidad en certeza." />
        <div data-reveal>
          <p className="lead-copy">Prestar un servicio de excelencia en proyectos de cimentación profunda, con calidad, seguridad y servicio como pilares de cada decisión.</p>
          <p>Aportamos una opción sólida y de alto desempeño para la infraestructura que impulsa una sociedad moderna y competitiva.</p>
        </div>
      </section>
      <MediaStrip eyebrow="Personas, equipo y terreno" items={[
        { name: 'equipo-campo', alt: 'Equipo CRS guiando una armadura de acero en obra', label: 'Trabajo coordinado' },
        { name: 'perforadora-vertical', alt: 'Perforadora CRS operando en un terreno de alta complejidad', label: 'Adaptación al terreno' },
        { name: 'maquinaria-panoramica', alt: 'Cuadrilla CRS junto a maquinaria de cimentación', label: 'Cultura de seguridad' },
      ]} />
      <section className="vision-panel">
        <ResponsiveImage name="perforadora" alt="Equipo y perforadora CRS en obra" sizes="(min-width: 900px) 45vw, 100vw" />
        <div data-reveal><p className="eyebrow light">Visión</p><h2>Ser referencia nacional en ejecución especializada.</h2><p>Construir relaciones duraderas a través de resultados confiables, tecnología y una cultura de seguridad real.</p></div>
      </section>
      <section className="values-section">
        <SectionHeading eyebrow="Nuestros valores" title="La profundidad también es humana." />
        <div className="values-grid">
          {values.map(([title, copy], index) => <article key={title} data-reveal><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}
        </div>
      </section>
    </>
  )
}

function ServicesPage() {
  return (
    <>
      <PageHero index="02" eyebrow="Servicios especializados" title="Capacidad para" italic="resolver profundo." description="Servicio integral, maquinaria certificada, herramientas de alto rendimiento y personal preparado para condiciones exigentes." image="perforacion-nocturna" tags={['Diseño ejecutable', 'Maquinaria', 'Control']} />
      <section className="services-detail">
        <SectionHeading eyebrow="Especialidades" title="Una solución para cada condición del terreno." />
        <div className="service-cards">
          {services.map((service) => <article key={service.number} data-reveal><div className="service-card-media"><ResponsiveImage name={service.image} alt={`${service.title} ejecutado por CRS`} sizes="(min-width: 900px) 50vw, 100vw" /><span>{service.number}</span></div><div className="service-card-copy"><h2>{service.title}</h2><p>{service.description}</p></div></article>)}
        </div>
      </section>
      <section className="capability-band texture-dots">
        <div data-reveal><p className="eyebrow light">Servicio integral</p><h2>Acero, concreto, maquinaria y ejecución coordinados.</h2></div>
        <ul data-reveal>
          <li>Retiro de material producto de perforación</li>
          <li>Fabricación de pilotes y armados de pila</li>
          <li>Suministro de tablestacado metálico</li>
          <li>Asesoría y soporte técnico en ejecución</li>
        </ul>
      </section>
    </>
  )
}

function ProjectsPage() {
  return (
    <>
      <PageHero index="03" eyebrow="Currículo empresarial" title="Obra real." italic="Escala nacional." description="Proyectos ejecutados con profesionalismo, pasión y rendimiento en infraestructura estratégica de México." image="obra-aerea" tags={['Infraestructura', 'Industria', 'Edificación']} />
      <section className="archive-section texture-grid">
        <div className="archive-intro"><p className="eyebrow">Selección de proyectos</p><p>Explora una selección visual de obra ejecutada. El archivo completo conserva las 33 publicaciones migradas desde WordPress con sus rutas públicas originales.</p></div>
        <ContentArchive entries={projectPosts} />
      </section>
    </>
  )
}

function NewsPage() {
  return (
    <>
      <PageHero index="04" eyebrow="Bitácora CRS" title="Campo, técnica" italic="y contexto." description="Obras recientes, decisiones de ingeniería e información útil alrededor de la cimentación profunda." image="piloteadora-cielo" tags={['Obra reciente', 'Técnica', 'Seguridad']} />
      <section className="news-section texture-grid">
        <SectionHeading eyebrow="Archivo" title="Lo que estamos construyendo y aprendiendo." />
        <div className="archive-filters" aria-label="Categorías del archivo" data-analytics-section="archive_filters"><a href="/category/noticias/">Proyectos y noticias <span>{projectPosts.length}</span></a><a href="/category/info-tecnica/">Información técnica <span>{technicalPosts.length}</span></a><a href="/category/video/">Video <span>1</span></a></div>
        <ContentArchive entries={allPosts} />
      </section>
    </>
  )
}

function ContactPage() {
  return (
    <>
      <PageHero index="05" eyebrow="Contacto" title="Tu proyecto merece" italic="un inicio sólido." description="Cuéntanos el reto. Atendemos proyectos de cimentación profunda y renta de maquinaria en toda la República Mexicana." image="aeropuerto-puerto-escondido" tags={['Cobertura nacional', 'Respuesta directa', 'Soporte técnico']} />
      <MediaStrip eyebrow="Listos para movilizar" items={[
        { name: 'perforacion-nocturna', alt: 'Dos perforadoras CRS en una obra de infraestructura', label: 'Infraestructura' },
        { name: 'obra-tijuana', alt: 'Frente de cimentación profunda en Tijuana', label: 'Edificación' },
        { name: 'equipo-campo', alt: 'Perforadora y armadura de acero en maniobra', label: 'Industria' },
      ]} />
      <section className="contact-section texture-grid">
        <div className="contact-statement" data-reveal><p className="eyebrow">Solicita una cotización</p><h2>Hablemos de suelo, estructura y tiempos.</h2><p>Para preparar una primera revisión, comparte ubicación, tipo de proyecto, estudio de mecánica de suelos y programa estimado si están disponibles.</p></div>
        <div className="contact-options">
          <a href={contact.whatsapp} target="_blank" rel="noreferrer" onClick={openContactWhatsApp} aria-haspopup="dialog" aria-controls={WHATSAPP_WIDGET_PANEL_ID} data-reveal data-analytics-manual="true" data-analytics-location="contact_options" data-analytics-label="Abrir widget de WhatsApp"><span>01 / Respuesta directa</span><h3>WhatsApp</h3><p>{contact.phoneLabel}</p><i>↗</i></a>
          <a href={`mailto:${contact.email}`} data-reveal data-analytics-location="contact_options" data-analytics-label="Correo"><span>02 / Documentación</span><h3>Correo</h3><p>{contact.email}</p><i>↗</i></a>
          <div data-reveal><span>03 / Atención</span><h3>Horario</h3><p>{contact.hours}<br />Cobertura nacional</p></div>
        </div>
      </section>
      <section className="quote-section" id="cotizar">
        <div className="quote-heading" data-reveal><p className="eyebrow light">Información inicial</p><h2>Prepara los datos de tu proyecto.</h2><p>Enviaremos la solicitud al equipo CRS y recibirás una confirmación inmediata por correo.</p></div>
        <QuoteForm />
      </section>
    </>
  )
}

function PrivacyPage() {
  return (
    <>
      <PageHero index="06" eyebrow="Legal" title="Política de" italic="privacidad." description="Términos sobre el uso y protección de la información proporcionada por las personas usuarias del sitio." image="cimentacion-profunda" />
      <article className="legal-copy">
        <h2>Información que es recogida</h2>
        <p>El sitio podrá recoger datos como nombre, información de contacto, información demográfica, hábitos de uso, datos del dispositivo y segmentación. Cuando sea necesario, podrá solicitar información específica para procesar pedidos, entregas o facturación.</p>
        <h2>Uso de la información</h2>
        <p>CRS Cimentaciones emplea la información para proporcionar el mejor servicio posible, mantener registros cuando aplique y mejorar productos y servicios. La empresa mantiene medidas orientadas a evitar accesos no autorizados.</p>
        <h2>Cookies</h2>
        <p>El sitio usa cookies y tecnologías similares mediante Google Tag Manager para administrar Google Analytics 4 y etiquetas de Google Ads. La medición se habilita de forma predeterminada al navegar; puedes bloquear o eliminar cookies desde la configuración de tu navegador.</p>
        <h2>Datos de medición</h2>
        <p>Registramos contexto de página e interacciones como navegación, profundidad de lectura, contacto, formularios y clics hacia WhatsApp. Tras confirmar una solicitud, correo, teléfono y nombre se normalizan y convierten a SHA-256 antes de incorporarse como datos proporcionados por el usuario; nunca se envían en texto plano a la capa de datos.</p>
        <h2>Enlaces a terceros</h2>
        <p>Los sitios externos enlazados tienen sus propias políticas y quedan fuera del control de CRS Cimentaciones. Se recomienda revisar sus términos antes de proporcionar información.</p>
        <h2>Control de información personal</h2>
        <p>CRS Cimentaciones no venderá, cederá ni distribuirá información personal sin consentimiento, salvo requerimiento legal. Para dudas o solicitudes, escribe a <a href={`mailto:${contact.email}`}>{contact.email}</a>.</p>
      </article>
    </>
  )
}

function CategoryPage({ entry }) {
  const category = entry.slug.endsWith('info-tecnica') ? 'Info técnica' : entry.slug.endsWith('video') ? 'Video' : 'Proyectos y noticias'
  const posts = allPosts.filter((post) => post.category === category)
  const hero = category === 'Info técnica' ? 'perforadora-vertical' : category === 'Video' ? 'equipo-campo' : 'obra-tijuana'

  return (
    <>
      <PageHero index="AR" eyebrow="Archivo CRS" title={category === 'Info técnica' ? 'Conocimiento' : 'Proyectos que'} italic={category === 'Info técnica' ? 'desde el terreno.' : 'dejan registro.'} description={entry.description} image={hero} tags={[category, `${posts.length} publicaciones`, 'Archivo completo']} />
      <section className="archive-section texture-grid"><div className="archive-intro"><p className="eyebrow">{category}</p><p>Consulta el archivo público migrado desde WordPress. Cada publicación conserva su ruta original, contenido y registro visual disponible.</p></div><ContentArchive entries={posts} /></section>
    </>
  )
}

function CampaignPage({ entry }) {
  const isRental = entry.slug === 'landing-renta-de-equipos'
  const fallback = isRental ? 'maquinaria-panoramica' : 'perforacion-nocturna'

  return (
    <>
      <section className="campaign-hero">
        <ContentImage image={entry.images[0]} fallback={fallback} alt={entry.title} className="campaign-hero-image" eager />
        <div className="campaign-shade" />
        <div className="campaign-copy"><p className="eyebrow light">CRS / Solución especializada</p><h1>{isRental ? 'Maquinaria lista para llegar al fondo.' : 'Cimentaciones profundas, respuesta integral.'}</h1><p>{entry.description}</p><a className="header-cta" href="#cotizar" data-analytics-event="cta_click" data-analytics-location="campaign_hero" data-analytics-label="Solicitar cotización">Solicitar cotización <span>↗</span></a></div>
      </section>
      <section className="campaign-content texture-grid"><div><p className="eyebrow">Capacidad CRS</p><h2>{entry.title}</h2></div><div>{entry.textBlocks.slice(0, 8).map((block) => <p key={block}>{block}</p>)}</div></section>
      {entry.images.length > 1 && <section className="campaign-gallery">{entry.images.slice(1, 5).map((image) => <ContentImage image={image} alt={image.alt} key={image.key} sizes="(min-width: 900px) 50vw, 100vw" />)}</section>}
      <section className="quote-section" id="cotizar"><div className="quote-heading"><p className="eyebrow light">Cotización</p><h2>Cuéntanos qué necesita la obra.</h2><p>Enviaremos la solicitud al equipo CRS y recibirás una confirmación inmediata por correo.</p></div><QuoteForm /></section>
    </>
  )
}

function ContentRoute({ summary }) {
  const [entry, setEntry] = useState(null)

  useEffect(() => {
    let active = true
    import('./data/content.generated.json').then(({ default: content }) => {
      if (active) setEntry(content.entries.find((item) => item.slug === summary.slug))
    })
    return () => { active = false }
  }, [summary.slug])

  if (!entry) return <div className="route-loading" role="status">Preparando archivo CRS…</div>
  if (entry.kind === 'post') {
    const related = allPosts.filter((item) => item.slug !== entry.slug && item.category === entry.category).slice(0, 3)
    return <ArticlePage entry={entry} related={related} />
  }
  return <CampaignPage entry={entry} />
}

const pages = {
  nosotros: AboutPage,
  servicios: ServicesPage,
  proyectos: ProjectsPage,
  noticias: NewsPage,
  contacto: ContactPage,
  'politica-de-privacidad': PrivacyPage,
}

export default function App() {
  const currentPath = window.location.pathname
  const slug = currentPath.split('/').filter(Boolean).join('/') || 'inicio'
  const Page = pages[slug]
  const entry = contentBySlug.get(slug)

  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined

    const context = gsap.context(() => {
      const header = document.querySelector('[data-header]')
      const heroElements = document.querySelectorAll('[data-hero]')
      if (header) gsap.from(header, { y: -30, opacity: 0, duration: 0.8, ease: 'power3.out' })
      if (heroElements.length) gsap.from(heroElements, { y: 36, opacity: 0, duration: 1, stagger: 0.1, ease: 'power3.out', delay: 0.15 })
      gsap.utils.toArray('[data-reveal]').forEach((element) => {
        gsap.from(element, {
          y: 42,
          opacity: 0,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: { trigger: element, start: 'top 88%', once: true },
        })
      })
    })

    return () => context.revert()
  }, [slug])

  let page
  if (slug === 'inicio') page = <HomePage />
  else if (Page) page = <Page />
  else if (entry?.kind === 'post') page = <ContentRoute summary={entry} />
  else if (entry?.kind === 'category') page = <CategoryPage entry={entry} />
  else if (entry?.kind === 'page') page = <ContentRoute summary={entry} />
  else page = <HomePage />

  return <Layout currentPath={currentPath}>{page}</Layout>
}
